import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './SimplePage.css';
import ParticleCanvas from '../components/ui/ParticleCanvas';

const VALUES = [
  { icon: '⚡', title: 'Speed First', desc: 'Every feature we ship is built around one goal: helping businesses respond faster and sell more.' },
  { icon: '🤝', title: 'Built for Businesses', desc: 'We are not a developer tool. We are built for shop owners, sales teams, and marketers who just want results.' },
  { icon: '🔒', title: 'Privacy by Default', desc: 'Your customer data stays yours. We never sell, share, or use your data for anything beyond running Zepliq.' },
  { icon: '🌍', title: 'Global from Day One', desc: 'Multi-language AI, global payment support, and 40+ countries using Zepliq on day one of launch.' },
];

const STATS = [
  { num: '5,000+', label: 'Businesses using Zepliq' },
  { num: '40+',    label: 'Countries worldwide' },
  { num: '10M+',   label: 'Conversations automated' },
  { num: '5 min',  label: 'Average setup time' },
];

export default function AboutPage() {
  return (
    <div className="simple-page">
      <SEO
        title="About Zepliq — The Team Behind AI WhatsApp Automation"
        description="Zepliq was founded in 2023 to give every business a 24/7 AI sales rep on WhatsApp. 5,000+ businesses, 40+ countries, 10M+ automated conversations. Learn our story."
        keywords="about Zepliq, Zepliq company, WhatsApp automation company, AI chatbot company, Zepliq team, Zepliq story, who is Zepliq"
        canonical="/about"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Zepliq',
          url: 'https://zepliq.com',
          foundingDate: '2023',
          description: 'AI-powered WhatsApp automation platform trusted by 5,000+ businesses in 40+ countries.',
          telephone: '+92-316-7441329',
          sameAs: [
            'https://www.youtube.com/@zepliq_ai',
            'https://www.linkedin.com/company/zepliq/',
          ],
        }}
      />

      <section className="simple-hero">
        <ParticleCanvas className="simple-hero__canvas" />
        <div className="simple-hero__blob simple-hero__blob--1" aria-hidden="true" />
        <div className="simple-hero__blob simple-hero__blob--2" aria-hidden="true" />
        <div className="container simple-hero__inner">
          <span className="badge badge-brand">About Us</span>
          <h1 className="display-xl simple-hero__title">
            We built Zepliq because<br />
            <span className="gradient-text">businesses deserved better.</span>
          </h1>
          <p className="text-lg simple-hero__sub">
            Every day, thousands of businesses lose leads and customers because nobody replied to their WhatsApp in time.
            We built Zepliq to fix that — permanently.
          </p>
        </div>
      </section>

      <section className="simple-section section">
        <div className="container">

          <div className="about-story">
            <div className="about-story__text">
              <span className="badge badge-accent">Our Story</span>
              <h2 className="display-lg about-story__title">
                Started with one frustrated shop owner.
              </h2>
              <p className="text-lg">
                In 2023, our founder was running an online store and watching dozens of WhatsApp leads go unanswered every day.
                Hiring a support team was expensive. Existing chatbot tools were complex and built for enterprise.
                So we built something different.
              </p>
              <p className="text-lg" style={{ marginTop: '1rem' }}>
                Zepliq launched with one mission: give every business — no matter the size — an AI that handles customer
                conversations on WhatsApp and email, so the team can focus on growth instead of inbox management.
              </p>
              <p className="text-lg" style={{ marginTop: '1rem' }}>
                Today Zepliq is used by 5,000+ businesses across 40+ countries, automating over 10 million conversations
                and helping businesses close more deals, confirm more orders, and never miss a lead again.
              </p>
            </div>
            <div className="about-story__stats">
              {STATS.map(s => (
                <div key={s.label} className="about-stat">
                  <span className="about-stat__num gradient-text">{s.num}</span>
                  <span className="about-stat__label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="simple-divider" />

          <div className="simple-header">
            <span className="badge badge-brand">Our Values</span>
            <h2 className="display-lg">What we stand for</h2>
          </div>
          <div className="simple-grid-4">
            {VALUES.map(v => (
              <div key={v.title} className="simple-card">
                <span className="simple-card__emoji">{v.icon}</span>
                <h3 className="simple-card__title">{v.title}</h3>
                <p className="simple-card__desc">{v.desc}</p>
              </div>
            ))}
          </div>

          <div className="simple-divider" />

          <div className="simple-cta-box">
            <h2 className="display-md">Ready to automate your business?</h2>
            <p className="text-lg">Join 5,000+ businesses already using Zepliq.</p>
            <div className="simple-cta-actions">
              <a href="https://dashboard.zepliq.com" className="btn btn-primary btn-lg">Start Free</a>
              <Link to="/contact" className="btn btn-secondary btn-lg">Talk to Us</Link>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
